import React, { useEffect, useState } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { consoleApi } from "../../services/consoleApiService";
import { useConsoleFetch } from "../../hooks/useConsoleFetch";
import {
  getApiErrorMessage,
  getAdminDisplayName,
  formatConsoleDateTime,
  toFeedbackImageDataUrl,
} from "../../utils/consoleUtils";
import { ErrorHandler } from "../../utils/errorHandler";
import ConsoleViewShell from "./ConsoleViewShell";
import { ConsolePageHeader } from "./ConsoleShared";
import { Button } from "../ui/button";
import { Textarea } from "../ui/textarea";
import "../../styles/console.css";

const STATUS_FILTERS = [
  { value: "", label: "All" },
  { value: "open", label: "Open" },
  { value: "in_progress", label: "In progress" },
  { value: "complete", label: "Complete" },
];

const STATUS_OPTIONS = [
  { value: "open", label: "Open" },
  { value: "in_progress", label: "In progress" },
  { value: "complete", label: "Complete" },
];

function descriptionPreview(text, maxLen = 120) {
  if (!text || typeof text !== "string") return "";
  const t = text.trim();
  return t.length <= maxLen ? t : t.slice(0, maxLen) + "…";
}

export default function FeedbackView({ onStatsChanged }) {
  const { user } = useAuth();
  const [statusFilter, setStatusFilter] = useState("");
  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState(null);
  const [noteText, setNoteText] = useState("");
  const [noteSubmitting, setNoteSubmitting] = useState(false);
  const [statusSubmitting, setStatusSubmitting] = useState(false);

  const listParams = { limit: 500 };
  if (statusFilter) listParams.status = statusFilter;

  const { data: list, loading, error, refetch } = useConsoleFetch(
    () => consoleApi.feedback.list(listParams).then((res) => (Array.isArray(res) ? res : [])),
    [statusFilter],
    { refetchOnWindowFocus: false }
  );
  const { data: stats, refetch: refetchStats } = useConsoleFetch(
    () => consoleApi.feedback.stats(),
    [],
    { refetchOnWindowFocus: false }
  );

  const items = list ?? [];

  useEffect(() => {
    if (!selectedId) {
      setDetail(null);
      setDetailError(null);
      return;
    }
    let cancelled = false;
    setDetailError(null);
    setDetailLoading(true);
    consoleApi.feedback
      .get(selectedId)
      .then((d) => {
        if (!cancelled) setDetail(d);
      })
      .catch((e) => {
        if (!cancelled) setDetailError(getApiErrorMessage(e));
      })
      .finally(() => {
        if (!cancelled) setDetailLoading(false);
      });
    return () => { cancelled = true; };
  }, [selectedId]);

  const noteAuthor = getAdminDisplayName(user);

  const handleStatusChange = async (newStatus) => {
    if (!selectedId || !detail) return;
    setDetailError(null);
    setStatusSubmitting(true);
    try {
      await consoleApi.feedback.update(selectedId, { status: newStatus });
      setDetail((d) => (d ? { ...d, status: newStatus } : null));
      refetch();
      await refetchStats();
      onStatsChanged?.();
    } catch (e) {
      ErrorHandler.logError("FeedbackView - updateStatus", e, { selectedId, newStatus });
      setDetailError(getApiErrorMessage(e));
    } finally {
      setStatusSubmitting(false);
    }
  };

  const handleAddNote = async (e) => {
    e.preventDefault();
    const text = (noteText || "").trim();
    if (!text || !selectedId) return;
    setDetailError(null);
    setNoteSubmitting(true);
    try {
      const updated = await consoleApi.feedback.addNote(selectedId, { text, author: noteAuthor });
      setDetail(updated);
      setNoteText("");
      refetch();
      await refetchStats();
      onStatsChanged?.();
    } catch (e) {
      ErrorHandler.logError("FeedbackView - addNote", e, { selectedId });
      setDetailError(getApiErrorMessage(e));
    } finally {
      setNoteSubmitting(false);
    }
  };

  return (
    <ConsoleViewShell loading={loading} error={error} loadingLabel="Loading feedback…">
      <div className="console-main-inner feedback-view">
        <div className="feedback-view-header">
          <div className="feedback-view-header-left">
            <ConsolePageHeader
              label="Ops"
              title="Feedback"
              sub="Admin-to-developer feedback. Submit from the side panel; view and update here."
            />
          </div>
          {stats && (
            <div className="feedback-view-kpis">
              <span className="feedback-kpi-text">Open <strong>{stats.open ?? 0}</strong></span>
              <span className="feedback-kpi-sep">·</span>
              <span className="feedback-kpi-text">In progress <strong>{stats.in_progress ?? 0}</strong></span>
              <span className="feedback-kpi-sep">·</span>
              <span className="feedback-kpi-text">Complete <strong>{stats.complete ?? 0}</strong></span>
              <span className="feedback-kpi-sep">·</span>
              <span className="feedback-kpi-text">Total <strong>{stats.total ?? 0}</strong></span>
            </div>
          )}
        </div>
        <div className="feedback-view-filters">
          {STATUS_FILTERS.map((f) => (
            <button
              key={f.value || "all"}
              type="button"
              className={`feedback-view-filter-btn ${statusFilter === f.value ? "active" : ""}`}
              onClick={() => setStatusFilter(f.value)}
            >
              {f.label}
            </button>
          ))}
        </div>
        <div className="feedback-view-layout">
          <div className="feedback-view-list">
            {items.length === 0 && !loading && (
              <p className="feedback-view-empty">No feedback yet. Use the Feedback panel on the right to add one.</p>
            )}
            {items.map((item) => (
              <div
                key={item.id}
                role="button"
                tabIndex={0}
                onClick={() => setSelectedId(item.id)}
                onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && setSelectedId(item.id)}
                className={`feedback-card ${selectedId === item.id ? "selected" : ""}`}
              >
                <div className="feedback-card-title">{item.title}</div>
                <div className="feedback-card-preview">{descriptionPreview(item.description)}</div>
                {(item.image_url || item.image_base64) && (
                  <div className="feedback-card-thumb-wrap">
                    <img
                      src={item.image_base64 ? toFeedbackImageDataUrl(item.image_base64, item.image_content_type) : item.image_url}
                      alt=""
                      className="feedback-card-thumb"
                    />
                  </div>
                )}
                <div className="feedback-card-meta">
                  <span className={`feedback-card-pill feedback-card-pill-status feedback-card-pill-${(item.status || "").replace("_", "-")}`}>
                    {(item.status || "").replace("_", " ")}
                  </span>
                  <span className={`feedback-card-pill feedback-card-pill-priority feedback-card-pill-priority-${item.priority || "medium"}`}>
                    {item.priority || "medium"}
                  </span>
                  <span className="feedback-card-meta-by">{item.created_by}</span>
                  <span className="feedback-card-meta-date">{formatConsoleDateTime(item.created_at)}</span>
                  {(item.notes?.length ?? 0) > 0 && (
                    <span className="feedback-card-notes-count">{item.notes.length} note{(item.notes.length ?? 0) !== 1 ? "s" : ""}</span>
                  )}
                </div>
              </div>
            ))}
          </div>
          <div className="feedback-view-detail">
            {detailError && <p className="feedback-view-detail-error" role="alert">{detailError}</p>}
            {!detailError && detailLoading && selectedId && <p className="feedback-view-detail-muted">Loading…</p>}
            {!detailError && !detailLoading && detail && (
              <div className="feedback-detail">
                <h3 className="feedback-detail-title">{detail.title}</h3>
                <div className="feedback-detail-meta">
                  <span className="feedback-detail-by">{detail.created_by}</span>
                  <span className="feedback-detail-date">{formatConsoleDateTime(detail.created_at)}</span>
                </div>
                {(detail.image_url || detail.image_base64) && (
                  <div className="feedback-detail-image-wrap">
                    <img
                      src={detail.image_base64 ? toFeedbackImageDataUrl(detail.image_base64, detail.image_content_type) : detail.image_url}
                      alt="Attached"
                      className="feedback-detail-image"
                    />
                  </div>
                )}
                <div className="feedback-detail-description">{detail.description}</div>
                <div className="feedback-detail-status-row">
                  <label htmlFor="feedback-detail-status" className="feedback-detail-label">Status</label>
                  <select
                    id="feedback-detail-status"
                    value={detail.status || "open"}
                    onChange={(e) => handleStatusChange(e.target.value)}
                    disabled={statusSubmitting}
                    className="feedback-detail-status-select"
                  >
                    {STATUS_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </div>
                <div className="feedback-notes">
                  <div className="feedback-notes-title">Notes</div>
                  <ul className="feedback-notes-list">
                    {(detail.notes || []).map((n, idx) => (
                      <li key={n.id || `note-${idx}`} className="feedback-note">
                        <div className="feedback-note-text">{n.text}</div>
                        <div className="feedback-note-meta">{n.author} · {formatConsoleDateTime(n.created_at)}</div>
                      </li>
                    ))}
                  </ul>
                  {((detail.notes || []).length === 0) && <p className="feedback-notes-empty">No notes yet.</p>}
                  <form onSubmit={handleAddNote} className="feedback-notes-form">
                    <Textarea
                      value={noteText}
                      onChange={(e) => setNoteText(e.target.value)}
                      placeholder="Add a note…"
                      className="feedback-notes-input"
                      rows={3}
                      maxLength={5000}
                      disabled={noteSubmitting}
                    />
                    <Button type="submit" disabled={noteSubmitting || !noteText.trim()} className="feedback-notes-submit">
                      {noteSubmitting ? "Adding…" : "Add note"}
                    </Button>
                  </form>
                </div>
              </div>
            )}
            {!detailError && !detailLoading && !detail && !selectedId && (
              <div className="feedback-view-detail-empty">
                <div className="feedback-view-detail-empty-icon">←</div>
                <div>Select a feedback item</div>
              </div>
            )}
          </div>
        </div>
      </div>
    </ConsoleViewShell>
  );
}
